import { useEffect, useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'
import { useLocales } from '@sevenrooms/locales'
import { useMeasure } from '@sevenrooms/ui-kit/hooks'
import { ReservationBreadcrumb, WidgetPortal } from '../../components'
import { useReleaseReservationHold, useReservationsRoute, useVenue, useAnalyticsSettings, useGroupOrCrossSellWidget } from '../../hooks'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import { useReservationFormState } from '../../store'
import { loadSearchPage } from '../../utils'
import { ExperiencesTab } from './ExperiencesTab'
import { PrivateEventsTab } from './PrivateEventsTab'
import { ReservationsTab } from './ReservationsTab'
import { ReservationsTabs } from './ReservationsTabs'
import { SearchContainer } from './SearchContainer'
import { VenueProfile } from './VenueProfile'

interface SearchProps {
  showTabs: boolean
  showPrivateEvents: boolean
  showExperiences: boolean
  isModifyRoute?: boolean
}

export function Search({ isModifyRoute, showTabs, showPrivateEvents, showExperiences }: SearchProps) {
  const {
    formState: { actual, searchTab },
  } = useReservationFormState()
  const venue = useVenue()
  const analyticsSettings = useAnalyticsSettings()
  const { formatMessage } = useLocales()
  const { clearQueryParams, hideAddress: hideAddressParam } = useReservationsRoute()
  const { releaseReservationHold } = useReleaseReservationHold()
  const { urlKey, name: venueName } = venue
  const [breadcrumbsRef, { height: breadcrumbsHeight }] = useMeasure({ debounce: 200 })
  const { isGroupWidget } = useGroupOrCrossSellWidget()

  const hideAddress = !!hideAddressParam || isGroupWidget
  const venueDescription = formatMessage(reservationWidgetMessages.resWidgetCrossSellVenueLongDescription)

  const showVenueDetails = useMemo(
    () => !isModifyRoute && (!hideAddress || !!sanitizeHtml(venueDescription, { allowedTags: [] }).trim()),
    [hideAddress, isModifyRoute, venueDescription]
  )

  useEffect(() => {
    // Send the Google Analytics event for search page load
    loadSearchPage(urlKey, analyticsSettings.fbPixelIdsAll, analyticsSettings.analyticsEventMapping.search)
  }, [urlKey, analyticsSettings.analyticsEventMapping.search, analyticsSettings.fbPixelIdsAll])

  useEffect(() => {
    releaseReservationHold()
    clearQueryParams('timeslot_id', 'timeslot_time', 'timeslot_experience_id')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchContent = useMemo(() => {
    if (showTabs && (showPrivateEvents || showExperiences)) {
      return (
        <SearchContainer.TabsContent>
          <ReservationsTabs isModifyRoute={isModifyRoute} showPrivateEvents={showPrivateEvents} showExperiences={showExperiences} />
        </SearchContainer.TabsContent>
      )
    }
    if (showPrivateEvents && searchTab === 'group_bookings') {
      return (
        <SearchContainer.Content breadcrumbsHeight={breadcrumbsHeight}>
          <PrivateEventsTab />
        </SearchContainer.Content>
      )
    }
    if (showExperiences && searchTab === 'experiences') {
      return (
        <SearchContainer.Content breadcrumbsHeight={breadcrumbsHeight}>
          <ExperiencesTab />
        </SearchContainer.Content>
      )
    }
    return (
      <SearchContainer.Content breadcrumbsHeight={breadcrumbsHeight}>
        <ReservationsTab isModifyRoute={isModifyRoute} showPrivateEvents={showPrivateEvents} />
      </SearchContainer.Content>
    )
  }, [breadcrumbsHeight, searchTab, showExperiences, showPrivateEvents, showTabs, isModifyRoute])

  return (
    <>
      <SearchContainer.MainContent showBreadcrumbs={!!breadcrumbsHeight} showTabs={showTabs}>
        {searchContent}
      </SearchContainer.MainContent>
      {isModifyRoute && (
        <WidgetPortal portalId="search-breadcrumbs">
          <ReservationBreadcrumb
            ref={breadcrumbsRef}
            venueName={venueName}
            partySize={actual?.maxGuests}
            timeIso={actual ? `${actual.date} ${actual.arrivalTime}` : undefined}
            label={formatMessage(reservationWidgetMessages.resWidgetCurrentReservationDetailsLabel)}
            timeSlotDescription={actual?.timeSlotDescription}
          />
        </WidgetPortal>
      )}
      {showVenueDetails && (
        <SearchContainer.VenueDetailsContainer hasSpacer={!showTabs}>
          <VenueProfile
            mapLink={hideAddress ? '' : venue.gmapsLink ?? ''}
            name={venue.name}
            address={hideAddress ? undefined : venue.address}
            cityState={hideAddress ? undefined : venue.cityState}
            postalCode={hideAddress ? undefined : venue.postalCode}
            gmapsEmbedUrl={hideAddress ? undefined : venue.gmapsEmbedUrl}
            key={urlKey}
            description={venueDescription}
          />
        </SearchContainer.VenueDetailsContainer>
      )}
    </>
  )
}
